/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// =========================================
// Utility per Compressione e Manipolazione Immagini
// =========================================

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  mimeType?: string;
}

class ImageCompressor {
  
  /**
   * Comprimi un'immagine mantenendo le proporzioni
   */
  async compressImage(
    file: File,
    options: CompressionOptions = {}
  ): Promise<{ blob: Blob; dataURL: string; reduction: number }> {
    const {
      maxWidth = 1920,
      maxHeight = 1080,
      quality = 0.85,
      mimeType = 'image/jpeg'
    } = options;

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          // Calcola nuove dimensioni mantenendo proporzioni
          let { width, height } = img;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          // Crea canvas
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          // Disegna immagine ridimensionata
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Impossibile ottenere context 2D'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Converti in blob
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Errore conversione blob'));
                return;
              }

              const dataURL = canvas.toDataURL(mimeType, quality);
              const originalSize = file.size;
              const compressedSize = blob.size;
              const reduction = Math.round((1 - compressedSize / originalSize) * 100);

              console.log(`✅ Compressione completata:`);
              console.log(`   Originale: ${this.formatBytes(originalSize)}`);
              console.log(`   Compressa: ${this.formatBytes(compressedSize)}`);
              console.log(`   Riduzione: ${reduction}%`);

              resolve({ blob, dataURL, reduction });
            },
            mimeType,
            quality
          );
        };

        img.onerror = () => reject(new Error('Errore caricamento immagine'));
        img.src = e.target?.result as string;
      };

      reader.onerror = () => reject(new Error('Errore lettura file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Aggiungi watermark a un'immagine
   */
  async addWatermark(
    imageDataURL: string,
    watermarkText: string,
    options: {
      position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
      fontSize?: number;
      color?: string;
      opacity?: number;
      logoPath?: string;
      logoHeight?: number;
    } = {}
  ): Promise<string> {
    const {
      position = 'bottom-right',
      fontSize = 16,
      color = '#ff6b35',
      opacity = 0.8,
      logoPath,
      logoHeight = 30
    } = options;

    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = async () => {
        console.log('📸 Immagine caricata per watermark:', img.width, 'x', img.height);
        
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Impossibile ottenere context 2D'));
          return;
        }

        // Disegna immagine originale
        ctx.drawImage(img, 0, 0);
        console.log('✅ Immagine disegnata su canvas');

        // Carica logo se specificato
        let logo: HTMLImageElement | null = null;
        if (logoPath) {
          try {
            logo = await this.loadImage(logoPath);
            console.log('✅ Logo caricato:', logo.width, 'x', logo.height);
          } catch (error) {
            console.warn('⚠️ Impossibile caricare logo, uso solo testo:', error);
          }
        }

        // Configura testo
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.fillStyle = color;
        ctx.globalAlpha = opacity;

        // Calcola dimensioni totali (logo + testo)
        const metrics = ctx.measureText(watermarkText);
        const textWidth = metrics.width;
        const padding = 10;
        const gap = 8; // Spazio tra logo e testo
        
        const logoWidth = logo ? (logo.width * logoHeight / logo.height) : 0;
        const totalWidth = logoWidth + (logo ? gap : 0) + textWidth;
        const totalHeight = Math.max(logoHeight, fontSize);

        let x = padding;
        let y = padding + fontSize;

        switch (position) {
          case 'top-right':
            x = canvas.width - totalWidth - padding;
            y = padding + totalHeight;
            break;
          case 'bottom-left':
            x = padding;
            y = canvas.height - padding - totalHeight / 2;
            break;
          case 'bottom-right':
            x = canvas.width - totalWidth - padding;
            y = canvas.height - padding - totalHeight / 2;
            break;
        }

        console.log('📍 Posizione watermark:', position, 'x:', x, 'y:', y);

        // Sfondo semi-trasparente
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        ctx.fillRect(x - 5, y - totalHeight - 5, totalWidth + 10, totalHeight + 10);

        // Disegna logo se disponibile
        if (logo) {
          const logoY = y - logoHeight;
          ctx.globalAlpha = 1.0; // Logo opaco
          ctx.drawImage(logo, x, logoY, logoWidth, logoHeight);
          console.log('✅ Logo disegnato');
          x += logoWidth + gap;
          ctx.globalAlpha = opacity; // Ripristina opacity per testo
        }

        // Testo
        ctx.fillStyle = color;
        ctx.fillText(watermarkText, x, y);
        
        console.log('✅ Watermark disegnato:', watermarkText);

        // Reset opacity
        ctx.globalAlpha = 1.0;

        const dataURL = canvas.toDataURL('image/jpeg', 0.9);
        console.log('✅ DataURL con watermark generato');
        resolve(dataURL);
      };

      img.onerror = () => reject(new Error('Errore caricamento immagine'));
      img.src = imageDataURL;
    });
  }

  /**
   * Carica un'immagine da un path
   */
  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Errore caricamento immagine'));
      img.src = src;
    });
  }

  /**
   * Formatta bytes in formato leggibile
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Verifica se il file è un'immagine
   */
  isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }

  /**
   * Verifica se il file è un video
   */
  isVideo(file: File): boolean {
    return file.type.startsWith('video/');
  }
}

// Esporta istanza singleton
export const imageCompressor = new ImageCompressor();
